/**
 * Lap Comparison Statistics
 *
 * Calculate aggregate statistics from preprocessed lap comparison data.
 */

import { LapComparisonStats } from './types'
import { sustainedPeakAbs } from '../../utils/sustainedPeak'
import { LEAN_PEAK_SUSTAIN_WINDOW_S } from '../../analysis/config/leanDisplay'

/**
 * Minimal data point shape required for stats calculation.
 * Both PreprocessedDataPoint and BaseLapDataPoint satisfy this.
 */
interface StatsDataPoint {
  speed: number
  /** Time from lap start in seconds (drives the sustained peak-lean stat). */
  time: number
  lateralG?: number
  leanAngle?: number
  acceleration?: number
}

/**
 * Minimal lap shape required for stats calculation.
 * Both PreprocessedLapComparison and CachedBaseLapData satisfy this.
 */
interface StatsLapInput {
  lapId: string
  lapTimeMs: number | undefined
  dataPoints: StatsDataPoint[]
}

/**
 * Calculate comparison statistics from laps with data points.
 * Accepts any lap shape that has lapId, lapTimeMs, and dataPoints with
 * speed/lateralG/leanAngle/acceleration fields.
 */
export function calculateComparisonStats(
  laps: StatsLapInput[]
): LapComparisonStats | null {
  if (laps.length === 0) return null

  let bestLapId = laps[0].lapId
  let bestLapTime = Infinity
  let worstLapTime = 0
  let topSpeed = 0
  let topSpeedLapId = laps[0].lapId
  let maxLateralG = 0
  let maxLateralGLapId = laps[0].lapId
  let maxLeanAngle = 0
  let maxLeanAngleLapId = laps[0].lapId
  let maxBraking = 0
  let maxBrakingLapId = laps[0].lapId
  let maxAcceleration = 0
  let maxAccelerationLapId = laps[0].lapId

  for (const lap of laps) {
    // Best/worst lap time
    if (lap.lapTimeMs && lap.lapTimeMs < bestLapTime) {
      bestLapTime = lap.lapTimeMs
      bestLapId = lap.lapId
    }
    if (lap.lapTimeMs && lap.lapTimeMs > worstLapTime) {
      worstLapTime = lap.lapTimeMs
    }

    // Scan data points for metrics
    for (const point of lap.dataPoints) {
      if (point.speed > topSpeed) {
        topSpeed = point.speed
        topSpeedLapId = lap.lapId
      }

      const absLateralG = Math.abs(point.lateralG ?? 0)
      if (absLateralG > maxLateralG) {
        maxLateralG = absLateralG
        maxLateralGLapId = lap.lapId
      }

      const accel = point.acceleration ?? 0
      if (accel < 0 && Math.abs(accel) > maxBraking) {
        maxBraking = Math.abs(accel)
        maxBrakingLapId = lap.lapId
      }
      if (accel > maxAcceleration) {
        maxAcceleration = accel
        maxAccelerationLapId = lap.lapId
      }
    }

    // Headline peak lean per lap = sustained (rolling-median) value — the
    // same presentation as the session/lap statistics, so the comparison
    // card and the lap stats never disagree. See LEAN_PEAK_SUSTAIN_WINDOW_S.
    const lapPeakLean = sustainedPeakAbs(
      lap.dataPoints.map((p) => p.leanAngle ?? 0),
      lap.dataPoints.map((p) => p.time * 1000),
      LEAN_PEAK_SUSTAIN_WINDOW_S * 1000
    )
    if (lapPeakLean > maxLeanAngle) {
      maxLeanAngle = lapPeakLean
      maxLeanAngleLapId = lap.lapId
    }
  }

  return {
    bestLapId,
    bestLapTime: bestLapTime === Infinity ? 0 : bestLapTime,
    worstLapTime,
    topSpeed,
    topSpeedLapId,
    maxLateralG,
    maxLateralGLapId,
    maxLeanAngle,
    maxLeanAngleLapId,
    maxBraking,
    maxBrakingLapId,
    maxAcceleration,
    maxAccelerationLapId,
  }
}
